import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { DecorationSet } from '@tiptap/pm/view';
import {
  AskAiSelectionHighlightAttributes,
  AskAiSelectionHighlightDecorationName,
  AskAiSelectionHighlightStorage,
} from './types';
import { decorationStorageManager } from './utils';
import {
  addAskAiSelectionHighlightCommand,
  removeAskAiSelectionHighlightCommand,
} from './utils';
import { InProgressStorageManager } from '../ask-ai/commands/utils/inprogress-storage-manager';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    [AskAiSelectionHighlightDecorationName]: {
      setAskAiSelectionHighlight: (
        attributes?: AskAiSelectionHighlightAttributes,
      ) => ReturnType;
      toggleAskAiSelectionHighlight: (
        attributes?: AskAiSelectionHighlightAttributes,
      ) => ReturnType;
      unsetAskAiSelectionHighlight: () => ReturnType;
    };
  }
}

export const AskAiSelectionHighlightDecoration = Extension.create<
  undefined,
  AskAiSelectionHighlightStorage
>({
  name: AskAiSelectionHighlightDecorationName,
  addStorage(): AskAiSelectionHighlightStorage {
    return {
      rangeSet: new WeakMap(),
      decorationSet: new WeakMap(),
    };
  },

  addCommands() {
    return {
      setAskAiSelectionHighlight: (attributes) => (props) => {
        const { from, to } = props.state.selection;

        return addAskAiSelectionHighlightCommand(from, to, attributes)(props);
      },
      toggleAskAiSelectionHighlight: (attributes) => (props) => {
        const { from, to } = props.state.selection;

        const { decorationExists } = decorationStorageManager(
          this.editor,
          props.state.tr,
        );

        if (decorationExists(from, to)) {
          return removeAskAiSelectionHighlightCommand(from, to)(props);
        } else {
          return addAskAiSelectionHighlightCommand(from, to, attributes)(props);
        }
      },
      unsetAskAiSelectionHighlight: () => (props) => {
        const { from, to } = props.state.selection;

        return removeAskAiSelectionHighlightCommand(from, to)(props);
      },
    };
  },

  onCreate() {
    this.editor.on('selectionUpdate', ({ transaction }) => {
      const { getInProgressCommands } = InProgressStorageManager(this.editor);

      if (
        transaction.getMeta('byAskAiAction') !== true &&
        getInProgressCommands().length === 0
      ) {
        const { removeAllDecorations } = decorationStorageManager(
          this.editor,
          transaction,
        );

        removeAllDecorations();
      }
    });
  },
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey(AskAiSelectionHighlightDecorationName),
        props: {
          decorations: () => this.storage.decorationSet.get(this.editor),
        },
        state: {
          init: () => DecorationSet.empty,
          apply: (tr, oldSet) => {
            const { getInProgressCommands } = InProgressStorageManager(
              this.editor,
            );

            if (
              getInProgressCommands().length > 0 ||
              tr.getMeta('byAskAiAction') === true
            ) {
              return (
                this.storage.decorationSet.get(this.editor) ??
                DecorationSet.empty
              );
            }

            return oldSet.map(tr.mapping, tr.doc);
          },
        },
      }),
    ];
  },

  onDestroy() {
    this.editor.off('selectionUpdate');
  },
});
